/**
 * ImpactBreakdown — tabbed midpoint dot-and-whisker chart (health /
 * biodiversity / economic).
 *
 * Each row is a category's contribution to the tab's endpoint, plotted on a
 * shared linear scale across the tab's rows:
 *   • a filled central dot (always),
 *   • plus, in Range mode, a thin floor→ceiling range line with small end caps.
 * Benefit rows read teal; harm / net-burden rows read amber — matching the
 * rest of the view. The right-hand value carries the endpoint's unit.
 */

import { useState } from "react";

export interface BreakdownRow {
  name: string;
  /** true = contribution is a benefit (teal); false = net burden (amber). */
  benefit: boolean;
  /** central magnitude (unsigned) — drives the shared scale + dot position. */
  central: number;
  /** central value, formatted for display. */
  centralText: string;
  /** floor/ceiling magnitudes (unsigned), present only in Range mode. */
  floor?: number;
  ceiling?: number;
  /** "floor–ceiling", formatted, present only in Range mode. */
  rangeText?: string;
}

export interface BreakdownTab {
  key: "h" | "b" | "e";
  label: string;
  /** Header line, e.g. "Contribution to human health · DALYs avoided". */
  header: string;
  /** Short unit printed next to each value, e.g. "DALYs" / "species·yr". */
  unit: string;
  rows: BreakdownRow[];
}

function DotWhiskers({ tab }: { tab: BreakdownTab }) {
  if (tab.rows.length === 0) {
    return <div className="empty">No breakdown categories for this lens.</div>;
  }
  // Shared linear scale across the tab's rows.
  const max = Math.max(
    1e-12,
    ...tab.rows.map((r) => Math.max(r.central, r.ceiling ?? 0)),
  );
  const pct = (v: number) => Math.max(0, Math.min(100, (v / max) * 100));

  return (
    <div>
      <div className="dwhead">{tab.header}</div>
      {tab.rows.map((r) => {
        const cp = pct(r.central);
        const lp = r.floor != null ? pct(r.floor) : null;
        const hp = r.ceiling != null ? pct(r.ceiling) : null;
        const hasRange = lp != null && hp != null;
        return (
          <div key={r.name} className={`dwrow ${r.benefit ? "pos" : "neg"}`}>
            <span className="nm">{r.name}</span>
            <span className="dwtrack">
              {hasRange && (
                <>
                  <span
                    className="dwline"
                    style={{ left: `${lp}%`, width: `${Math.max(0, hp! - lp!)}%` }}
                  />
                  <span className="dwcap" style={{ left: `${lp}%` }} />
                  <span className="dwcap" style={{ left: `${hp}%` }} />
                </>
              )}
              <span className="dwdot" style={{ left: `${cp}%` }} />
            </span>
            <span className="val">
              {r.centralText} <span className="u">{tab.unit}</span>
              {r.rangeText && (
                <span className="r">
                  ({r.rangeText} {tab.unit})
                </span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function ImpactBreakdown({ tabs }: { tabs: BreakdownTab[] }) {
  const [active, setActive] = useState<"h" | "b" | "e">(tabs[0]?.key ?? "h");
  const current = tabs.find((t) => t.key === active) ?? tabs[0];

  return (
    <div className="card">
      <div className="ch">
        <h2>Impact breakdown</h2>
        <div className="tabs" role="tablist">
          {tabs.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={t.key === active}
              onClick={() => setActive(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="cb">{current && <DotWhiskers tab={current} />}</div>
    </div>
  );
}
